import { CreateAccountFromRequestModal } from '@app/components/accounts/CreateAccount/CreateAccountFromRequestModal';
import { PageTitle } from '@app/components/various/PageTitle';
import { AccountRequestStatus } from '@app/constants/requests';
import { AccountRequestHeader } from '@app/pages/AccountRequestDetailsPage/AccountRequestHeader/AccountRequestHeader';
import { AccountRequestInformation } from '@app/pages/AccountRequestDetailsPage/AccountRequestInformation';
import { compiledPaths } from '@app/paths';
import { useAccountRequestDetails } from '@app/services/hooks/account/useAccountRequestDetails';
import { useAccountRequestStatus } from '@app/services/hooks/account/useAccountRequestStatus';
import { useConfirmationGuard } from '@app/utils/hooks';
import { useModalToggle } from '@app/utils/hooks/useModalToggle';
import { useUrlLookbookProductId } from '@app/utils/hooks/useUrlLookbookProductId';
import { useUrlLookbookVariantId } from '@app/utils/hooks/useUrlLookbookVariantId';
import { isDefined } from '@app/utils/is';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { AccountRequestWishlist } from './AccountRequestWishlist/AccountRequestWishlist';

export const AccountRequestDetailsPage = () => {
  const location = useLocation();
  const productId = useUrlLookbookProductId();
  const variantId = useUrlLookbookVariantId();
  const { t } = useTranslation(['requests', 'accounts']);
  const { accountRequestId } = useParams<{ accountRequestId: string }>();
  const confirmationGuard = useConfirmationGuard();
  const { mutate: updateAccountRequestStatus, isPending: isLoadingAccountRequestStatus } = useAccountRequestStatus();
  const { showModal: showCreateAccountModal, isOpen: isShowCreateAccountModalOpen, hideModal: hideCreateAccountModal } = useModalToggle();

  const navigate = useNavigate();

  const { data: accountRequestDetails, isLoading: isLoadingAccountRequestDetails } = useAccountRequestDetails({ id: accountRequestId });
  const accountRequsetLookbook = accountRequestDetails?.source?.lookbook;
  const accountRequsetWishlist = accountRequestDetails?.source?.wishlist;
  const { from } = (location?.state as { from?: Location }) ?? { from: undefined };
  const fallbackPreviousPath = compiledPaths.ACCOUNT_REQUESTS({});
  const previousPath = from ?? fallbackPreviousPath;

  const goToAccount = () => {
    if (!isDefined(accountRequestDetails?.accountId)) {
      return;
    }

    navigate(compiledPaths.ACCOUNT_DETAILS({ accountId: accountRequestDetails.accountId }));
  };

  const companyName = accountRequestDetails?.company ?? '';

  const handleDeclineAccountRequest = () =>
    confirmationGuard(() => ({
      content: t('requests:modal_decline_request.description'),
      okText: t('accounts:decline'),
      onOk: () =>
        isDefined(accountRequestId) &&
        updateAccountRequestStatus({
          companyName,
          id: accountRequestId,
          status: AccountRequestStatus.Declined,
        }),
      title: t('requests:modal_decline_request.title'),
    }))();

  const handleArchiveAccountRequest = () =>
    confirmationGuard(() => ({
      content: t('requests:modal_archive_request.description'),
      okText: t('requests:archive'),
      onOk: () =>
        isDefined(accountRequestId) &&
        updateAccountRequestStatus({
          companyName,
          id: accountRequestId,
          status: AccountRequestStatus.Archived,
        }),
      title: t('requests:modal_archive_request.title'),
    }))();

  const handlePendingAccountRequest = () => {
    if (!isDefined(accountRequestId)) {
      return;
    }

    updateAccountRequestStatus({
      companyName,
      id: accountRequestId,
      status: AccountRequestStatus.Pending,
    });
  };

  return (
    <PageTitle title={`${t('requests:account_request')} - ${companyName}`}>
      <>
        <div className="mx-auto w-full max-w-screen-xl px-6 pb-16 pt-3">
          <AccountRequestHeader
            status={accountRequestDetails?.status}
            createdAt={accountRequestDetails?.createdAt ?? '-'}
            isLoading={isLoadingAccountRequestDetails}
            isLoadingStatus={isLoadingAccountRequestStatus}
            previousPath={previousPath}
            goToAccount={goToAccount}
            onDecline={handleDeclineAccountRequest}
            onArchive={handleArchiveAccountRequest}
            onPending={handlePendingAccountRequest}
            onCreateAccount={showCreateAccountModal}
          />

          <AccountRequestInformation isLoading={isLoadingAccountRequestDetails} accountRequestDetails={accountRequestDetails} />
          {isDefined(accountRequsetLookbook) && isDefined(accountRequsetWishlist) && (
            <AccountRequestWishlist
              lookbookId={accountRequsetLookbook}
              wishlistId={accountRequsetWishlist}
              productId={productId}
              variantId={variantId}
            />
          )}
        </div>
        <CreateAccountFromRequestModal
          isOpen={isShowCreateAccountModalOpen}
          onSuccess={hideCreateAccountModal}
          onClose={hideCreateAccountModal}
          accountRequestId={accountRequestId}
        />
      </>
    </PageTitle>
  );
};
